import { Component, OnInit, OnDestroy } from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Form, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { SessionContextService } from "projects/packcert-web/src/app/context/session-context.service";
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Message, SpinnerService, UtilsService } from 'shared-components';
import { EtiquetadoContextService } from "../../context/etiquetado-context.service";
import { EtiquetadoService } from "../../services/etiquetado.service";
import { Router } from "@angular/router";
@Component({
    selector: 'app-mantenimiento-envases',
    templateUrl: './mantenimiento-envases.component.html'
})
export class MantenimientoEnvasesComponent implements OnInit, OnDestroy {

    private readonly _DEFAULT_ITEMS_PER_PAGE = 10;
    private _filters;
    public filtersVisible = false;
    public filtersForm: UntypedFormGroup;

    public envases;
    public envaseId;
    public toCloseSpinner = 0;

    public disabledEtiquetadores: Subject<boolean> = new Subject();

    public mensajes = new Array<Message>();
    public showError: boolean;

    public deleteModal = false;
    public newEditModal = false;

    public itemsPerPage = this._DEFAULT_ITEMS_PER_PAGE;
    public currentPage = 0;
    public totalItems: number;
    public readonly visiblePages = 3;

    public orderBy: string;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly _router: Router,
        private _fb: UntypedFormBuilder,
        private readonly _sessionContext: SessionContextService,
        private readonly _spinnerService: SpinnerService,
        private readonly _etiquetadoService: EtiquetadoService,
        private readonly _etiquetadoContext: EtiquetadoContextService,
        private readonly _dataProvider: DataProvider,
        private readonly _utilsService: UtilsService,
        private readonly _translate: TranslateService
    ) { }

    ngOnInit() {
        this.filtersForm = this._fb.group({
            etiquetador: [''],
            envase: [''],
            fechaInicio: [''],
            fechaFin: [''],
            dateType: ['false']
        });

        this._etiquetadoContext.mantenimientoEnvasesFilters = null;

        this.filtersForm.get('fechaInicio').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());
        this.filtersForm.get('fechaFin').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());

        this.getEnvases();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get role() {
        return this._sessionContext.sessionUser.role;
    }

    public get claims() {
        return this._sessionContext.sessionUser.lsClaimTypes;
    }

    public get etiquetadores() {
        if (this._dataProvider.etiquetadores && this._dataProvider.etiquetadores.length === 1) {
            this.filtersForm.get('etiquetador').patchValue(this._dataProvider.etiquetadores[0]);
            this.filtersForm.get('etiquetador').disable();
            this.disabledEtiquetadores.next(true);
        }

        return this._dataProvider.etiquetadores;
    }

    public get destinos() {
        return this._dataProvider.destinos;
    }

    public get defaultLanguage() {
      return this._translate.getDefaultLang();
    }

    public get isSmallScreen(){
      return this._sessionContext.isSmallScreen();
    }

    public pageChange(page) {
        this._etiquetadoContext.mantenimientoEnvasesFilters.numPagina = this.currentPage = page - 1;
        this.getEnvases();
    }

    public itemsPerPageChange(items) {
        this._etiquetadoContext.mantenimientoEnvasesFilters.numPagina = this.currentPage = 0;
        this._etiquetadoContext.mantenimientoEnvasesFilters.tamPagina = this.itemsPerPage = items;
        this.getEnvases();
    }

    public validDates() {
        if (this.filtersForm.get('fechaInicio').value && this.filtersForm.get('fechaFin').value) {
            if (this.checkDates(this.filtersForm.get('fechaInicio').value, this.filtersForm.get('fechaFin').value)) {
                this.clearDatesValidators();
            } else {
                this.filtersForm.get('fechaInicio').setErrors(['Invalid date']);
                this.filtersForm.get('fechaFin').setErrors(['Invalid date']);
            }
            if (this.filtersForm.get('fechaInicio').invalid || this.filtersForm.get('fechaFin').invalid) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.fecha.error.finAnteriorInicio' });
            } else {
                this.showError = false;
                this.mensajes = [];
            }
        } else {
            this.clearDatesValidators();
            this.showError = false;
            this.mensajes = [];
        }
    }

    private clearDatesValidators() {
        if (this.filtersForm.get('fechaFin').errors && this.filtersForm.get('fechaFin').errors.includes('Invalid date')) {
            this.filtersForm.get('fechaFin').clearValidators();
            this.filtersForm.get('fechaFin').updateValueAndValidity();
        }
        if (this.filtersForm.get('fechaInicio').errors && this.filtersForm.get('fechaInicio').errors.includes('Invalid date')) {
            this.filtersForm.get('fechaInicio').clearValidators();
            this.filtersForm.get('fechaInicio').updateValueAndValidity();
        }
    }

    private checkDates(inicio, fin) {
        const inicioD = new Date(inicio);
        const finD = new Date(fin);
        return inicioD <= finD;
    }

    public checkValuesForm(): boolean {
        return (this.filtersForm.get('etiquetador').value  && this.filtersForm.get('etiquetador').enabled ||
            this.filtersForm.get('envase').value ||
            this.filtersForm.get('fechaInicio').value ||
            this.filtersForm.get('fechaFin').value) ? true : false;
    }

    public resetForm() {
        this.filtersForm.reset({
            etiquetador: this.filtersForm.get('etiquetador').disabled ? this.filtersForm.get('etiquetador').value : null,
            dateType: 'false'
        });
    }

    public filtrar() {
        this.validDates();
        
        if (this.filtersForm.valid) {
            this.applyFilters();
        }
    }

    private applyFilters() {
        this.currentPage = 0;
        this._etiquetadoContext.mantenimientoEnvasesFilters = {
            etiquetadorId: this.filtersForm.get('etiquetador').value ? this.filtersForm.get('etiquetador').value.id : '',
            desEnvase: this.filtersForm.get('envase').value ? this.filtersForm.get('envase').value : '',
            fechaInicio: this.filtersForm.get('fechaInicio').value ?
                this._utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaInicio').value) : '',
            fechaFin: this.filtersForm.get('fechaFin').value ?
                this._utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaFin').value) : '',
            fechaActualizacion: this.filtersForm.get('dateType').value,
            cargaRegistroEnUso: true,
            creadoAuto: false,
            numPagina: this.currentPage,
            tamPagina: this.itemsPerPage,
        }

        this.getEnvases();
    }

    public getEnvases() {
        if (this._etiquetadoContext.mantenimientoEnvasesFilters) {
            this._filters = this._etiquetadoContext.mantenimientoEnvasesFilters;
        } else {
            this.applyFilters();
        }
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._etiquetadoService.getEnvases(this._filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            this.totalItems = resp.paginacionMeta.totalCount;
            this.envases = resp.lista;
        }, (error) => {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
        });

    }

    public create() {
        this.envaseId = null;
        this.newEditModal = true;
    }

    public update(envase) {
        this.envaseId = envase.id;
        this.newEditModal = true;
    }

    public delete() {
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._etiquetadoService.deleteEnvase(this.envaseId).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then(() => {
            this.close();
            this.pushMessage('global.success.itemDeleted');
            this.getEnvases();
        });
    }

    public askDelete(envase) {
        this.envaseId = envase.id;
        this.deleteModal = true;
    }

    public close() {
        this.deleteModal = false;
        this.newEditModal = false;
    }

    public refresh(value) {
        this.newEditModal = false;
        if (value) {
            this.getEnvases();
        }
    }

    public pushMessage(mensaje) {
        this.showError = true;
        this.mensajes.push({ class: 'success', message: mensaje });
    }

    private checkCloseSpinner() {
        this.toCloseSpinner -= 1;
        if (this.toCloseSpinner === 0) {
            this._spinnerService.hideSpinner();
        }
    }

    public getIcon(prop: string) {
      let iconClass = 'fa fa-sort';
      if (this._filters && this._filters.orderItems && this._filters.orderItems[0].orderProperty.indexOf(prop) != -1) {
          iconClass = this._filters.orderItems[0].orderByDescending === true ? 'fa fa-sort-down' : 'fa fa-sort-up';
      }
      return iconClass;
    }

    public toOrderBy(columnFilter, idColumn) {
      if (this.envases && this.envases.length > 0) {
          if (this._etiquetadoContext.mantenimientoEnvasesFilters) {
              this._filters = this._etiquetadoContext.mantenimientoEnvasesFilters;
          }
      }

      if (!this.orderBy || this.orderBy.includes('DESC')) {
          this.orderBy = 'ASC' + idColumn;
          this._filters.orderItems = [
              {
              orderProperty: columnFilter,
              orderByDescending: false
              }
          ];
      } else if (this.orderBy.includes('ASC')) {
          this.orderBy = 'DESC' + idColumn;
          this._filters.orderItems = [
              {
                  orderProperty: columnFilter,
                  orderByDescending: true
              }
          ];
      }

      this._spinnerService.showSpinner();
      this.toCloseSpinner += 1;
      this._etiquetadoService.getEnvases(this._filters).toPromise().finally(() => {
		this.checkCloseSpinner();
    }).then((resp) => {
        this.envases = resp.lista;
    });
  }

  private downloadPdf(mimeType: string) {
    this.toCloseSpinner += 1;
      this._etiquetadoService.getEnvasesPdf(this._filters).toPromise().finally(() => {
		this.checkCloseSpinner();
      }).then((resp) => {
        const downloadLink = document.createElement('a');
        downloadLink.href = mimeType + resp.base64Archivo;
        downloadLink.download = resp.nombreArchivo;
        downloadLink.click();
      }, (error) => {
        this.showError = true;
        this.mensajes.push({ class: 'error', message: 'global.error.file' });
      });
  }

  private downloadCsv(mimeType: string) {
    this.toCloseSpinner += 1;
      this._etiquetadoService.getEnvasesCsv(this._filters).toPromise().finally(() => {
		this.checkCloseSpinner();
      }).then((resp) => {
        const downloadLink = document.createElement('a');
        downloadLink.href = mimeType + resp.base64Archivo;
        downloadLink.download = resp.nombreArchivo;
        downloadLink.click();
      }, (error) => {
        this.showError = true;
        this.mensajes.push({ class: 'error', message: 'global.error.file' });
      });
  }

  public exportPdfOrCsv(tipo) {
    if (this.envases && this.envases.length > 0) {
      if (this._etiquetadoContext.mantenimientoEnvasesFilters) {
        var mimeType;
        this._filters = this._etiquetadoContext.mantenimientoEnvasesFilters;
        this._filters.tamPagina = 0;
        if (tipo === 'csv') {
            this._filters.tipoArchivo = 'Csv';
            mimeType = 'data:text/csv;base64,';
        } else {
            this._filters.tipoArchivo = 'Pdf';
            mimeType = 'data:application/pdf;base64,';
        }
        }

        this._spinnerService.showSpinner();
        tipo === 'csv' ? this.downloadCsv(mimeType) : this.downloadPdf(mimeType);
      
    } else {
        this.showError = true;
        this.mensajes.push({ class: 'error', message: 'global.error.exportNoData' });
    }
  }

  public openDocHelp() {
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    this._etiquetadoService.getDocHelp('Labeling_Packages.pdf').toPromise().finally(() => {
        this.checkCloseSpinner();
    }).then((resp) => {
        localStorage.setItem('base64', resp.base64Archivo);
        const url = this._router.serializeUrl(this._router.createUrlTree(['viewPdf']));
        window.open(url, '_blank');
    });
  }

  public trackByEnvaseId(index: number, envase: { id: number }): number {
    return envase.id;
  }
}
